/**
 * MarketMoversTab — Index constituent contribution breakdown
 *
 * Fixes applied (round-3):
 *  1. Multi-date sort tracks which date's column was clicked (sortDateKey)
 *  2. Pill order: Live > D-1 > D-2... ; Today pill removed (same data as Live)
 *  3. CHG (absolute ₹) shown for every date column, not just Live
 *  4. Single-live column order: LTP > CHG > %CHG > PTS > CONTRIB% > %OF MOVE
 *  5. Multi-date per-date columns: CHG > %CHG > PTS(live) > CONTRIB% > %OF MOVE
 *  6. Font/alignment: name fontWeight 400, explicit left-align on ticker+name cells
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getMarketMoversLive, getMarketMoversHistory, getMarketMoversAllDates } from '../../api/client';
import { Ticker } from '../ui/index';
import InfoBtn from '../ui/InfoBtn';
import { useSidePanel } from '../../context/SidePanelContext';

// ── Module-level cache (survives tab switches, 1-hour TTL) ─────────────────────
const _moversCache = {};
const CACHE_TTL    = 60 * 60 * 1000;

function getCached(dateKey, indexId) {
  const entry = _moversCache[`${dateKey}:${indexId}`];
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) { delete _moversCache[`${dateKey}:${indexId}`]; return null; }
  return entry.data;
}
function setCache(dateKey, indexId, data) {
  _moversCache[`${dateKey}:${indexId}`] = { data, ts: Date.now() };
}

// ── Layout constants ──────────────────────────────────────────────────────────
const TOP_PILLS   = 78;   // H_NAV(44) + H_SUBTAB(34) — flush below StickySubTabs
const PILLS_H     = 34;
const TOP_HEADERS = TOP_PILLS + PILLS_H;

// ── Fixed left column widths: # | Ticker | Name | Wt% ─────────────────────────
const WR = 28;
const WK = 78;
const WN = 148;
const WW = 52;

const INDICES = [
  { id: 'NIFTY50',   label: 'NIFTY 50' },
  { id: 'SENSEX',    label: 'SENSEX' },
  { id: 'BANKNIFTY', label: 'Bank NIFTY' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const upColor = v =>
  v == null ? 'var(--ink-3)' : v > 0 ? 'var(--up, #27ae60)' : v < 0 ? 'var(--dn, #c0392b)' : 'var(--ink-2)';

function heatBg(v) {
  if (v == null) return 'transparent';
  const op = Math.min(0.52, Math.abs(v) / 0.4 * 0.52);
  return v > 0 ? `rgba(26,106,26,${op.toFixed(2)})` : v < 0 ? `rgba(136,30,16,${op.toFixed(2)})` : 'transparent';
}

function sign(v, dec = 2) {
  if (v == null) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return (n > 0 ? '+' : '') + n.toFixed(dec);
}

function fmtPrice(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso || iso === 'live') return 'Live';
  const [, mo, dy] = iso.split('-');
  return `${parseInt(mo)}/${parseInt(dy)}`;
}

function InfoPopover({ text }) {
  return <InfoBtn>{text}</InfoBtn>;
}

const SUMMARY_BAND_INFO = `Summary band methodology:
TOP DRAGS: the 3 constituents with the most negative CONTRIB% (percentage-point drag on the index move).
TOP DRIVERS: the 3 constituents with the most positive CONTRIB%.
CONCENTRATION: measures how much of the day's move is explained by the top 3 contributors (by absolute CONTRIB%).
  Formula: sum(|top-3 CONTRIB%|) ÷ sum(|all CONTRIB%|).
  Concentrated ≥ 75% | Mixed 40–75% | Broad-based < 40%.`;

const CONTRIB_INFO = `CONTRIB% — Weighted contribution to the index's daily % move.

Formula: (NSE index weight %) × (stock % change) ÷ 100

Example: HDFC has 10% weight in NIFTY 50 and is up +2% today → CONTRIB% = +0.20%.

Interpretation: the signed number of percentage points that stock adds to (or subtracts from) the index return. The sum of all CONTRIB% equals the index's total % change.`;

const SHARE_INFO = `%OF MOVE — Each stock's signed share of today's total index move.

Formula: CONTRIB% ÷ Σ(all CONTRIB%) × 100

The column sums to exactly 100%. Positive values = contributed to the direction of today's move; negative values = worked against it.

Useful for identifying concentration: if the top 3 stocks account for 70%+ of the move, the day is "concentrated" rather than broad-based.`;

// ── Summary band computation (pure, used by cards + IndexTable) ───────────────
function computeSummaryBand(rows) {
  if (!rows || !rows.length) return null;
  const sorted       = [...rows].sort((a, b) => (b.contrib_pct ?? 0) - (a.contrib_pct ?? 0));
  const top3Drivers  = sorted.slice(0, 3).filter(r => (r.contrib_pct ?? 0) > 0);
  const top3Drags    = [...rows].sort((a, b) => (a.contrib_pct ?? 0) - (b.contrib_pct ?? 0)).slice(0, 3).filter(r => (r.contrib_pct ?? 0) < 0);
  const sumAbsAll    = rows.reduce((s, r) => s + Math.abs(r.contrib_pct ?? 0), 0);
  const top3AbsSum   = [...rows].sort((a, b) => Math.abs(b.contrib_pct ?? 0) - Math.abs(a.contrib_pct ?? 0))
    .slice(0, 3).reduce((s, r) => s + Math.abs(r.contrib_pct ?? 0), 0);
  const concRatio    = sumAbsAll > 0 ? top3AbsSum / sumAbsAll : 0;
  const concLabel    = concRatio >= 0.75 ? 'Concentrated' : concRatio >= 0.40 ? 'Mixed' : 'Broad-based';
  const concColor    = concRatio >= 0.75 ? 'var(--amber-ink)' : 'var(--ink-2)';
  return { top3Drivers, top3Drags, concLabel, concColor, concRatio };
}

// ── Summary Cards Row — three cards (NIFTY 50 / SENSEX / Bank NIFTY) ─────────
function SummaryCardsRow({ selectedDates, loadingDates, dateData }) {
  const { open: openPanel } = useSidePanel();
  const isLive = selectedDates[0] === 'live';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 14 }}>
      {INDICES.map(({ id, label }) => {
        const loading = loadingDates.size > 0;
        let rows = [];
        let indexInfo = null;
        for (const dk of selectedDates) {
          const d = dateData[dk]?.[id];
          if (d?.rows?.length) { rows = d.rows; }
          if (!indexInfo && d?.index) { indexInfo = d.index; }
        }
        const band = computeSummaryBand(rows);

        const ltp     = indexInfo?.ltp;
        const chgPct  = indexInfo?.chg_pct;
        const prev    = ltp != null && chgPct != null ? ltp / (1 + chgPct / 100) : null;
        const chgRaw  = ltp != null && prev ? ltp - prev : null;
        const upColor = chgRaw == null ? 'var(--ink)' : chgRaw >= 0 ? 'var(--up-field-ink, #27ae60)' : 'var(--dn-field-ink, #c0392b)';

        return (
          <div key={id}
            onClick={() => openPanel({ name: id })}
            style={{
              background: 'var(--surface-2)', border: '.5px solid var(--hair)',
              borderRadius: 6, padding: '10px 12px', minWidth: 0,
              cursor: 'pointer',
            }}>
            {/* Header: label + status pill + price */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em',
                  textTransform: 'uppercase', color: 'var(--ink-2)' }}>
                  {label}
                </span>
                <span style={{
                  fontSize: 9.5, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                  background: isLive ? 'rgba(34,197,94,.12)' : 'var(--surface-3,var(--surface))',
                  color: isLive ? 'var(--up-field-ink, #27ae60)' : 'var(--ink-3)',
                  border: isLive ? '.5px solid rgba(34,197,94,.3)' : '.5px solid var(--hair-strong)',
                  letterSpacing: '.04em',
                }}>
                  {isLive ? 'LIVE' : 'CLOSED'}
                </span>
                {loading && !band && <span style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 400 }}>Loading…</span>}
              </div>
              {ltp != null ? (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <span style={{ fontFamily: 'var(--font-data)', fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                    {fmtPrice(ltp)}
                  </span>
                  {chgRaw != null && (
                    <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: upColor }}>
                      {chgRaw >= 0 ? '+' : ''}{chgRaw.toFixed(2)} ({chgPct >= 0 ? '+' : ''}{Number(chgPct).toFixed(2)}%)
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>No price data</div>
              )}
            </div>
            {!band ? (
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>No data yet</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 4 }}>Top Drags</div>
                  {band.top3Drags.length === 0
                    ? <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
                    : band.top3Drags.map(r => (
                      <div key={r.symbol} style={{ display: 'flex', gap: 5, alignItems: 'baseline', marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, color: 'var(--ink)', fontFamily: 'var(--font-data)', fontSize: 11 }}>{r.symbol}</span>
                        <span style={{ color: 'var(--dn, #c0392b)', fontFamily: 'var(--font-data)', fontSize: 10.5 }}>{(r.contrib_pct ?? 0).toFixed(3)}%</span>
                      </div>
                    ))}
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 4 }}>Top Drivers</div>
                  {band.top3Drivers.length === 0
                    ? <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
                    : band.top3Drivers.map(r => (
                      <div key={r.symbol} style={{ display: 'flex', gap: 5, alignItems: 'baseline', marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, color: 'var(--ink)', fontFamily: 'var(--font-data)', fontSize: 11 }}>{r.symbol}</span>
                        <span style={{ color: 'var(--up, #27ae60)', fontFamily: 'var(--font-data)', fontSize: 10.5 }}>+{(r.contrib_pct ?? 0).toFixed(3)}%</span>
                      </div>
                    ))}
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 4 }}>Concentration</div>
                  <div style={{ fontWeight: 700, color: band.concColor, fontSize: 11 }}>{band.concLabel}</div>
                  <div style={{ color: 'var(--ink-3)', fontSize: 10, marginTop: 2 }}>{(band.concRatio * 100).toFixed(0)}% of move by top 3</div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── IndexTable ────────────────────────────────────────────────────────────────
function IndexTable({ indexDef, selectedDates, loadingDates, dateData, showSummaryBand = true }) {
  const { id, label } = indexDef;

  const headerRef = useRef(null);
  const bodyRef   = useRef(null);

  // Table collapsed by default; filter pill for top-N
  const [tableOpen,   setTableOpen]   = useState(false);
  const [filterN,     setFilterN]     = useState(10); // 10 | 20 | null (All)

  // sortDateKey: which date's columns were clicked; null = use first available
  // Default sort: abs(contrib_share) descending (= %OF MOVE descending)
  const [sortCol,     setSortCol]     = useState('share');
  const [sortDir,     setSortDir]     = useState('desc');
  const [sortDateKey, setSortDateKey] = useState(null);

  const syncScroll = useCallback(e => {
    if (headerRef.current) headerRef.current.scrollLeft = e.target.scrollLeft;
  }, []);

  // dateKey = null for non-date columns (rank/ticker/name/wt)
  const handleSort = useCallback((col, dateKey = null) => {
    setSortCol(prev => {
      if (prev === col) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
      } else {
        setSortDir(['rank', 'wt', 'ticker', 'name'].includes(col) ? 'asc' : 'desc');
      }
      setSortDateKey(dateKey);
      return col;
    });
  }, []);

  // Sort indicator: only lights up if both col and dateKey match
  const si = (col, dateKey = null) =>
    (sortCol === col && sortDateKey === dateKey) ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // ── Date column specs ──────────────────────────────────────────────────────
  const dateCols = useMemo(() => selectedDates.map(dateKey => {
    const isLive    = dateKey === 'live';
    const d         = dateData[dateKey]?.[id];
    const rows      = d?.rows ?? [];
    const indexInfo = d?.index ?? null;
    const loading   = loadingDates.has(dateKey);
    const indexChgPct = isLive
      ? (indexInfo?.chg_pct ?? null)
      : (rows.length ? rows.reduce((s, r) => s + (r.contrib_pct ?? 0), 0) : null);
    return { dateKey, isLive, rows, indexInfo, indexChgPct, loading };
  }), [selectedDates, dateData, id]);

  const singleLive = selectedDates.length === 1 && selectedDates[0] === 'live';
  const anyLoading = dateCols.some(d => d.loading);

  // Base rows from first dataset with data
  const baseRows = useMemo(() => {
    for (const dc of dateCols) { if (dc.rows.length) return dc.rows; }
    return [];
  }, [dateCols]);

  // Sort: use the date column that was clicked for numeric lookups
  const sortedRows = useMemo(() => {
    if (!baseRows.length) return baseRows;
    const sortDc = sortDateKey
      ? (dateCols.find(dc => dc.dateKey === sortDateKey && dc.rows.length) ?? dateCols.find(dc => dc.rows.length))
      : dateCols.find(dc => dc.rows.length);
    const getVal = row => {
      const dcRow = sortDc?.rows.find(r => r.symbol === row.symbol) ?? row;
      switch (sortCol) {
        case 'rank':    return row.rank_in_idx ?? 9999;
        case 'ticker':  return (row.symbol ?? '').toLowerCase();
        case 'name':    return (row.company_name ?? '').toLowerCase();
        case 'wt':      return row.weightage ?? 0;
        case 'price':   return dcRow.ltp ?? 0;
        case 'chg':     return dcRow.chg ?? 0;
        case 'chg_pct': return dcRow.chg_pct ?? 0;
        case 'pts':     return dcRow.contrib_pts ?? 0;
        case 'contrib': return dcRow.contrib_pct ?? 0;
        // share sorts by abs value so the most impactful movers surface first
        case 'share':   return Math.abs(dcRow.contrib_share ?? 0);
        default:        return 0;
      }
    };
    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...baseRows].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (typeof va === 'string') return dir * va.localeCompare(vb);
      return dir * (va - vb);
    });
    return filterN ? sorted.slice(0, filterN) : sorted;
  }, [baseRows, sortCol, sortDir, sortDateKey, dateCols, filterN]);

  // Totals per date
  const totals = useMemo(() => dateCols.map(dc => ({
    sumContrib: dc.rows.reduce((s, r) => s + (r.contrib_pct ?? 0), 0),
    sumPts:     dc.rows.reduce((s, r) => s + (r.contrib_pts ?? 0), 0),
    sumShare:   dc.rows.reduce((s, r) => s + (r.contrib_share ?? 0), 0),
  })), [dateCols]);

  // ── Style bases ────────────────────────────────────────────────────────────
  const thBase = {
    background: 'var(--surface-2)',
    borderBottom: '.5px solid var(--hair)',
    fontSize: 11, fontWeight: 700, letterSpacing: '.05em',
    textTransform: 'uppercase', color: 'var(--ink-3)',
    padding: '5px 6px', whiteSpace: 'nowrap',
    cursor: 'pointer', userSelect: 'none',
    textAlign: 'right',
  };
  const tdBase = {
    padding: '4px 6px',
    borderBottom: '.5px solid var(--hair)',
    whiteSpace: 'nowrap', fontSize: 11,
    fontFamily: 'var(--font-data)',
  };

  // Sticky td/th helpers
  const stickyTd = (left, width, extra = {}) => ({
    position: 'sticky', left, zIndex: 2,
    background: 'var(--surface)',
    width, minWidth: width, maxWidth: width,
    overflow: 'hidden', textOverflow: 'ellipsis',
    ...tdBase, ...extra,
  });
  const stickyTh = (left, width, extra = {}) => ({
    ...thBase,
    position: 'sticky', left, zIndex: 6,
    width, minWidth: width, maxWidth: width,
    textAlign: 'left',
    ...extra,
  });

  // ColGroup:
  //   Single-live right cols: LTP | CHG | %CHG | PTS | CONTRIB% | %OF MOVE  (6)
  //   Multi-date per-date: CHG | %CHG | PTS(live) | CONTRIB% | %OF MOVE  → 4 hist / 5 live
  const ColGroup = () => (
    <colgroup>
      <col style={{ width: WR, minWidth: WR }}/>
      <col style={{ width: WK, minWidth: WK }}/>
      <col style={{ width: WN, minWidth: WN }}/>
      <col style={{ width: WW, minWidth: WW }}/>
      {singleLive ? (
        <><col/><col/><col/><col/><col/><col/></>
      ) : dateCols.map(dc => (
        <React.Fragment key={dc.dateKey}>
          <col/><col/>
          {dc.isLive && <col/>}
          <col/><col/>
        </React.Fragment>
      ))}
    </colgroup>
  );

  // ── Index summary row ─────────────────────────────────────────────────────
  const renderSummaryRow = () => {
    const bg = 'var(--surface-2)';
    return (
      <tr style={{ background: bg }}>
        <td style={stickyTd(0, WR, { background: bg, textAlign: 'center', color: 'var(--ink-3)', fontSize: 11 })}>—</td>
        <td style={stickyTd(WR, WK, { background: bg, color: 'var(--ink-3)', fontSize: 11, fontFamily: 'var(--font-data)', textAlign: 'left' })}>{id}</td>
        <td style={stickyTd(WR+WK, WN, { background: bg, fontWeight: 700, color: 'var(--ink)', fontSize: 12, fontFamily: 'inherit', textAlign: 'left' })}>{label}</td>
        <td style={stickyTd(WR+WK+WN, WW, { background: bg, textAlign: 'right', color: 'var(--ink-3)' })}>100%</td>

        {singleLive ? (() => {
          const dc   = dateCols[0];
          const info = dc?.indexInfo;
          const ltp  = info?.ltp;
          const chg  = info?.chg_pct;
          const prev = ltp && chg != null ? ltp / (1 + chg / 100) : null;
          const chgRaw = ltp && prev ? ltp - prev : null;
          const t = totals[0] ?? { sumContrib: 0, sumPts: 0, sumShare: 0 };
          const hasData = dc && !dc.loading && dc.rows.length > 0;
          return (
            <>
              <td style={{ ...tdBase, textAlign: 'right', color: 'var(--ink)', fontWeight: 700 }}>{ltp ? fmtPrice(ltp) : '—'}</td>
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(chgRaw), fontWeight: 600 }}>{chgRaw != null ? sign(chgRaw) : '—'}</td>
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(chg), fontWeight: 700 }}>{chg != null ? `${sign(chg)}%` : (dc?.loading ? '…' : '—')}</td>
              {/* Σ PTS — compare to CHG */}
              <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: hasData ? upColor(t.sumPts) : 'var(--ink-3)' }}>
                {hasData ? sign(t.sumPts, 1) : '—'}
              </td>
              {/* Σ CONTRIB% — compare to %CHG */}
              <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: hasData ? upColor(t.sumContrib) : 'var(--ink-3)' }}>
                {hasData ? `${sign(t.sumContrib, 3)}%` : '—'}
              </td>
              {/* Σ %OF MOVE — should be ≈ 100% */}
              <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: hasData ? upColor(t.sumShare) : 'var(--ink-3)' }}>
                {hasData ? `${sign(t.sumShare, 1)}%` : '—'}
              </td>
            </>
          );
        })() : dateCols.map((dc, i) => {
          const chg  = dc.indexChgPct;
          const info = dc.indexInfo;
          const ltp  = info?.ltp;
          const prev = ltp && chg != null ? ltp / (1 + chg / 100) : null;
          const chgRaw = ltp && prev ? ltp - prev : null;
          const t = totals[i] ?? { sumContrib: 0, sumPts: 0, sumShare: 0 };
          const hasData = !dc.loading && dc.rows.length > 0;
          return (
            <React.Fragment key={dc.dateKey}>
              {/* CHG column */}
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(chgRaw), fontWeight: 600, borderLeft: '.5px solid var(--hair)' }}>
                {chgRaw != null ? sign(chgRaw) : '—'}
              </td>
              {/* %CHG column */}
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(chg), fontWeight: 700 }}>
                {chg != null ? `${sign(chg)}%` : (dc.loading ? '…' : '—')}
              </td>
              {/* PTS (live only) */}
              {dc.isLive && (
                <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: hasData ? upColor(t.sumPts) : 'var(--ink-3)' }}>
                  {hasData ? sign(t.sumPts, 1) : '—'}
                </td>
              )}
              {/* Σ CONTRIB% */}
              <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: hasData ? upColor(t.sumContrib) : 'var(--ink-3)' }}>
                {hasData ? `${sign(t.sumContrib, 3)}%` : '—'}
              </td>
              {/* Σ %OF MOVE */}
              <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: hasData ? upColor(t.sumShare) : 'var(--ink-3)' }}>
                {hasData ? `${sign(t.sumShare, 1)}%` : '—'}
              </td>
            </React.Fragment>
          );
        })}
      </tr>
    );
  };

  // ── Stock row ──────────────────────────────────────────────────────────────
  const renderStockRow = (baseRow, rowIdx) => {
    const sym = baseRow.symbol;
    const wt  = baseRow.weightage;

    return (
      <tr key={sym}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <td style={stickyTd(0, WR, { textAlign: 'center', color: 'var(--ink-3)', fontSize: 11 })}>
          {baseRow.rank_in_idx ?? rowIdx + 1}
        </td>
        <td style={stickyTd(WR, WK, { fontFamily: 'var(--font-data)', fontSize: 11, textAlign: 'left' })}>
          <Ticker name={sym}/>
        </td>
        <td style={stickyTd(WR+WK, WN, { fontFamily: 'inherit', fontWeight: 400, color: 'var(--ink)', textAlign: 'left' })}>
          {baseRow.company_name}
        </td>
        <td style={stickyTd(WR+WK+WN, WW, { textAlign: 'right', color: 'var(--ink-3)' })}>
          {wt != null ? `${Number(wt).toFixed(2)}%` : '—'}
        </td>

        {singleLive ? (() => {
          const dc  = dateCols[0];
          const r   = dc.rows.find(x => x.symbol === sym) ?? {};
          const cbg = heatBg(r.contrib_pct);
          return (
            <>
              <td style={{ ...tdBase, textAlign: 'right', color: 'var(--ink)' }}>{r.ltp != null ? fmtPrice(r.ltp) : '—'}</td>
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(r.chg) }}>{r.chg != null ? sign(r.chg) : '—'}</td>
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(r.chg_pct), fontWeight: 600 }}>{r.chg_pct != null ? `${sign(r.chg_pct)}%` : '—'}</td>
              {/* PTS before CONTRIB% */}
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(r.contrib_pts) }}>{r.contrib_pts != null ? sign(r.contrib_pts, 1) : '—'}</td>
              <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600, color: upColor(r.contrib_pct) }}>{r.contrib_pct != null ? `${sign(r.contrib_pct, 3)}%` : '—'}</td>
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(r.contrib_share), fontSize: 11 }}>{r.contrib_share != null ? `${sign(r.contrib_share, 1)}%` : '—'}</td>
            </>
          );
        })() : dateCols.map(dc => {
          const r   = dc.rows.find(x => x.symbol === sym) ?? {};
          const cbg = heatBg(r.contrib_pct);
          return (
            <React.Fragment key={dc.dateKey}>
              {/* CHG — shown for all dates */}
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(r.chg), borderLeft: '.5px solid var(--hair)' }}>
                {r.chg != null ? sign(r.chg) : (dc.loading ? '…' : '—')}
              </td>
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(r.chg_pct), fontWeight: 600 }}>
                {r.chg_pct != null ? `${sign(r.chg_pct)}%` : (dc.loading ? '…' : '—')}
              </td>
              {/* PTS live only */}
              {dc.isLive && (
                <td style={{ ...tdBase, textAlign: 'right', color: upColor(r.contrib_pts) }}>
                  {r.contrib_pts != null ? sign(r.contrib_pts, 1) : '—'}
                </td>
              )}
              <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600, background: cbg, color: upColor(r.contrib_pct) }}>
                {r.contrib_pct != null ? `${sign(r.contrib_pct, 3)}%` : '—'}
              </td>
              <td style={{ ...tdBase, textAlign: 'right', color: upColor(r.contrib_share), fontSize: 11 }}>
                {r.contrib_share != null ? `${sign(r.contrib_share, 1)}%` : '—'}
              </td>
            </React.Fragment>
          );
        })}
      </tr>
    );
  };

  // ── Summary band ─────────────────────────────────────────────────────────
  const summaryBand = useMemo(() => {
    const dc = dateCols.find(dc => dc.rows.length);
    return dc ? computeSummaryBand(dc.rows) : null;
  }, [dateCols]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ marginBottom: 28 }}>
      {/* Sticky header unit */}
      <div style={{ position: 'sticky', top: TOP_HEADERS, zIndex: 15, background: 'var(--paper)' }}>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '5px 0 3px',
          borderBottom: '.5px solid var(--hair)',
          background: 'var(--paper)',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
            {label}
          </span>
          <InfoPopover text={SUMMARY_BAND_INFO}/>
          {anyLoading && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>⏳</span>}
        </div>

        <div ref={headerRef} style={{ overflowX: 'hidden', background: 'var(--surface-2)', borderBottom: '.5px solid var(--hair)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <ColGroup/>
            <thead>
              {/* Date group row (multi-date only) */}
              {!singleLive && (
                <tr>
                  <th colSpan={4} style={{ ...thBase, textAlign: 'left', cursor: 'default' }}/>
                  {dateCols.map(dc => (
                    <th key={dc.dateKey}
                      colSpan={dc.isLive ? 5 : 4}
                      style={{ ...thBase, textAlign: 'center', borderLeft: '.5px solid var(--hair)', color: 'var(--amber-ink)', cursor: 'default' }}>
                      {fmtDate(dc.dateKey)}
                    </th>
                  ))}
                </tr>
              )}
              {/* Column headers */}
              <tr>
                <th onClick={() => handleSort('rank')}   style={stickyTh(0, WR, { textAlign: 'center' })}>#{si('rank')}</th>
                <th onClick={() => handleSort('ticker')} style={stickyTh(WR, WK)}>TICKER{si('ticker')}</th>
                <th onClick={() => handleSort('name')}   style={stickyTh(WR+WK, WN)}>NAME{si('name')}</th>
                <th onClick={() => handleSort('wt')}     style={stickyTh(WR+WK+WN, WW, { textAlign: 'right' })}>WT%{si('wt')}</th>
                {singleLive ? (
                  <>
                    <th onClick={() => handleSort('price')}   style={{ ...thBase }}>PRICE{si('price')}</th>
                    <th onClick={() => handleSort('chg')}     style={{ ...thBase }}>CHG{si('chg')}</th>
                    <th onClick={() => handleSort('chg_pct')} style={{ ...thBase }}>%CHG{si('chg_pct')}</th>
                    <th onClick={() => handleSort('pts')}     style={{ ...thBase }}>PTS{si('pts')}</th>
                    <th onClick={() => handleSort('contrib')} style={{ ...thBase }}>CONTRIB%{si('contrib')}<InfoBtn align="right">{CONTRIB_INFO}</InfoBtn></th>
                    <th onClick={() => handleSort('share')}   style={{ ...thBase }}>%OF MOVE{si('share')}<InfoBtn align="right">{SHARE_INFO}</InfoBtn></th>
                  </>
                ) : dateCols.map(dc => (
                  <React.Fragment key={dc.dateKey}>
                    <th onClick={() => handleSort('chg', dc.dateKey)}     style={{ ...thBase, borderLeft: '.5px solid var(--hair)' }}>CHG{si('chg', dc.dateKey)}</th>
                    <th onClick={() => handleSort('chg_pct', dc.dateKey)} style={{ ...thBase }}>%CHG{si('chg_pct', dc.dateKey)}</th>
                    {dc.isLive && <th onClick={() => handleSort('pts', dc.dateKey)} style={{ ...thBase }}>PTS{si('pts', dc.dateKey)}</th>}
                    <th onClick={() => handleSort('contrib', dc.dateKey)} style={{ ...thBase }}>CONTRIB%{si('contrib', dc.dateKey)}<InfoBtn align="right">{CONTRIB_INFO}</InfoBtn></th>
                    <th onClick={() => handleSort('share', dc.dateKey)}   style={{ ...thBase }}>%OF MOVE{si('share', dc.dateKey)}<InfoBtn align="right">{SHARE_INFO}</InfoBtn></th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {renderSummaryRow()}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Summary band (hidden when cards row is shown above) ── */}
      {showSummaryBand && summaryBand && !anyLoading && (
        <div style={{
          display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start',
          padding: '7px 10px', background: 'var(--surface-2)',
          border: '.5px solid var(--hair)', borderTop: 'none',
          fontSize: 11,
        }}>
          {/* Top Drags */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 140 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 2 }}>Top Drags</span>
            {summaryBand.top3Drags.length === 0
              ? <span style={{ color: 'var(--ink-3)' }}>—</span>
              : summaryBand.top3Drags.map(r => (
                <span key={r.symbol} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, color: 'var(--ink)', fontFamily: 'var(--font-data)' }}>{r.symbol}</span>
                  <span style={{ color: 'var(--dn, #c0392b)', fontFamily: 'var(--font-data)' }}>{(r.contrib_pct ?? 0).toFixed(3)}%</span>
                </span>
              ))}
          </div>
          {/* Top Drivers */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 140 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 2 }}>Top Drivers</span>
            {summaryBand.top3Drivers.length === 0
              ? <span style={{ color: 'var(--ink-3)' }}>—</span>
              : summaryBand.top3Drivers.map(r => (
                <span key={r.symbol} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, color: 'var(--ink)', fontFamily: 'var(--font-data)' }}>{r.symbol}</span>
                  <span style={{ color: 'var(--up, #27ae60)', fontFamily: 'var(--font-data)' }}>+{(r.contrib_pct ?? 0).toFixed(3)}%</span>
                </span>
              ))}
          </div>
          {/* Concentration */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 110 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 2 }}>Concentration</span>
            <span style={{ fontWeight: 700, color: summaryBand.concColor }}>{summaryBand.concLabel}</span>
            <span style={{ color: 'var(--ink-3)', fontSize: 10 }}>{(summaryBand.concRatio * 100).toFixed(0)}% of move by top 3</span>
          </div>
        </div>
      )}

      {/* ── Filter pills + table toggle ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: '.5px solid var(--hair)' }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-3)', marginRight: 2 }}>Show</span>
        {[10, 20, null].map(n => (
          <button key={String(n)} className={`pill${filterN === n ? ' on' : ''}`}
            onClick={() => setFilterN(n)}
            style={{ fontSize: 11, padding: '2px 10px' }}>
            {n === null ? 'All' : `Top ${n}`}
          </button>
        ))}
        <div style={{ flex: 1 }}/>
        <button
          onClick={() => setTableOpen(o => !o)}
          style={{ fontSize: 11, padding: '2px 10px', background: 'none', border: '.5px solid var(--hair-strong)',
            borderRadius: 4, cursor: 'pointer', color: 'var(--ink-2)' }}>
          {tableOpen ? '▲ Hide table' : '▼ Show full table'}
        </button>
      </div>

      {/* Scrollable body — hidden unless tableOpen */}
      {tableOpen && <div ref={bodyRef} onScroll={syncScroll}
        style={{
          overflowX: 'auto',
          borderLeft: '.5px solid var(--hair)',
          borderRight: '.5px solid var(--hair)',
          borderBottom: '.5px solid var(--hair)',
          borderRadius: '0 0 8px 8px',
        }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 11 }}>
          <ColGroup/>
          <tbody>
            {anyLoading && sortedRows.length === 0
              ? Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: singleLive ? 10 : (4 + dateCols.reduce((s, dc) => s + (dc.isLive ? 5 : 4), 0)) }).map((_, j) => (
                    <td key={j} style={{ ...tdBase }}>
                      <div style={{ height: 9, width: j === 0 ? 18 : j < 3 ? 80 : 40, borderRadius: 3, background: 'var(--surface-3)', opacity: .5 }}/>
                    </td>
                  ))}
                </tr>
              ))
              : sortedRows.length === 0 && !anyLoading
              ? (
                <tr>
                  <td colSpan={20} style={{ ...tdBase, padding: '16px 8px', textAlign: 'center', color: 'var(--ink-3)' }}>
                    {selectedDates[0] === 'live'
                      ? 'Kite not connected — connect Kite to see live data'
                      : 'No constituent data. Go to Admin → Index Constituents and click "Seed missing".'}
                  </td>
                </tr>
              )
              : sortedRows.map((row, ri) => renderStockRow(row, ri))
            }
          </tbody>
        </table>
      </div>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function MarketMoversTab() {
  const [selectedDates, setSelectedDates] = useState(['live']);
  const [availDates,    setAvailDates]     = useState([]);
  const [loadingDates,  setLoadingDates]   = useState(new Set());
  const [dateData,      setDateData]       = useState({});

  // Fetch available EOD dates — exclude today (same as Live) and weekends (server filters, but guard here too)
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    getMarketMoversAllDates(6).then(res => {
      const dates = (res.dates ?? []).filter(d => d !== today);
      setAvailDates(dates.slice(0, 5));
    }).catch(() => {});
  }, []);

  // Fetch a date key — honours module-level cache
  const fetchDate = useCallback(async (dateKey) => {
    const isLive = dateKey === 'live';
    const allCached = INDICES.every(({ id }) => getCached(dateKey, id) !== null);
    if (allCached) {
      const results = {};
      INDICES.forEach(({ id }) => { results[id] = getCached(dateKey, id); });
      setDateData(prev => ({ ...prev, [dateKey]: results }));
      return;
    }

    setLoadingDates(prev => new Set([...prev, dateKey]));
    const results = {};
    await Promise.all(
      INDICES.map(async ({ id }) => {
        const cached = getCached(dateKey, id);
        if (cached) { results[id] = cached; return; }
        try {
          const res = isLive
            ? await getMarketMoversLive(id)
            : await getMarketMoversHistory(id, dateKey);
          setCache(dateKey, id, res);
          results[id] = res;
        } catch (e) {
          results[id] = { error: String(e), rows: [], index: null };
        }
      })
    );
    setDateData(prev => ({ ...prev, [dateKey]: results }));
    setLoadingDates(prev => { const s = new Set(prev); s.delete(dateKey); return s; });
  }, []);

  useEffect(() => { fetchDate('live'); }, [fetchDate]);

  const toggleDate = useCallback((dateKey) => {
    setSelectedDates(prev => {
      const has = prev.includes(dateKey);
      if (has) {
        if (prev.length === 1) return prev;
        return prev.filter(d => d !== dateKey);
      }
      if (!dateData[dateKey] && !loadingDates.has(dateKey)) fetchDate(dateKey);
      return [...prev, dateKey];
    });
  }, [dateData, loadingDates, fetchDate]);

  const handleRefresh = useCallback(() => {
    selectedDates.forEach(dateKey => {
      INDICES.forEach(({ id }) => { delete _moversCache[`${dateKey}:${id}`]; });
    });
    setDateData(prev => {
      const next = { ...prev };
      selectedDates.forEach(d => delete next[d]);
      return next;
    });
    selectedDates.forEach(d => fetchDate(d));
  }, [selectedDates, fetchDate]);

  const anyLoading = loadingDates.size > 0;

  // Always render columns in canonical order: live first, then dates newest → oldest
  // regardless of the order the user clicked the pills.
  const sortedSelectedDates = useMemo(() =>
    [...selectedDates].sort((a, b) => {
      if (a === 'live') return -1;
      if (b === 'live') return 1;
      return b.localeCompare(a); // descending date order
    }),
  [selectedDates]);

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* Sticky pills bar */}
      <div style={{
        position: 'sticky', top: TOP_PILLS, zIndex: 20,
        background: 'var(--paper)',
        height: PILLS_H,
        display: 'flex', alignItems: 'center', gap: 6,
        borderBottom: '.5px solid var(--hair)',
        paddingRight: 8,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', color: 'var(--ink-3)', textTransform: 'uppercase', marginRight: 4 }}>
          VIEW
        </span>

        {/* Live pill first, then historical dates newest → oldest (Today excluded) */}
        {(['live']).concat(availDates).map(dateKey => {
          const active = selectedDates.includes(dateKey);
          return (
            <button key={dateKey} onClick={() => toggleDate(dateKey)}
              style={{
                padding: '3px 11px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 600,
                background: active ? 'var(--amber-ink)' : 'var(--surface-2)',
                color: active ? '#fff' : 'var(--ink-2)',
                opacity: loadingDates.has(dateKey) ? 0.7 : 1,
              }}>
              {dateKey === 'live' ? 'Live' : fmtDate(dateKey)}
              {loadingDates.has(dateKey) && ' ⟳'}
            </button>
          );
        })}

        {availDates.length === 0 && (
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>No EOD history yet</span>
        )}

        <div style={{ flex: 1 }}/>

        <button onClick={handleRefresh} disabled={anyLoading}
          style={{
            padding: '4px 12px', borderRadius: 5, border: 'none',
            cursor: anyLoading ? 'default' : 'pointer',
            fontSize: 11, fontWeight: 600,
            background: 'var(--surface-2)', color: 'var(--ink-2)',
            opacity: anyLoading ? 0.6 : 1,
          }}>
          {anyLoading ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
      </div>

      <div style={{ paddingTop: 8 }}>
        {/* Summary cards — scrollable, not sticky */}
        <SummaryCardsRow
          selectedDates={sortedSelectedDates}
          loadingDates={loadingDates}
          dateData={dateData}
        />

        {INDICES.map(def => (
          <IndexTable
            key={def.id}
            indexDef={def}
            selectedDates={sortedSelectedDates}
            loadingDates={loadingDates}
            dateData={dateData}
            showSummaryBand={false}
          />
        ))}
      </div>
    </div>
  );
}
